import { useState } from "react";
import { format, isValid } from "date-fns";
import { BookOpen, ShieldCheck, Pencil, ChevronDown, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export type ClinicalSource = {
  label?: string;
  title?: string;
  publisher?: string;
  organization?: string;
  url?: string;
  year?: number | string;
  accessed_at?: string;
};

type Grade = "editorial" | "clinician-reviewed" | "guideline-backed" | string;

interface Props {
  sources?: unknown;
  clinical_grade?: Grade | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  next_review_at?: string | null;
  compact?: boolean;
}

const GRADE_META: Record<
  string,
  { label: string; icon: typeof Pencil; className: string; blurb: string }
> = {
  guideline_backed: {
    label: "Guideline-backed",
    icon: ShieldCheck,
    className: "bg-emerald-100 text-emerald-900 border-emerald-200",
    blurb:
      "This original Her Season explanation has been checked against recognised clinical guidance.",
  },
  "guideline-backed": {
    label: "Guideline-backed",
    icon: ShieldCheck,
    className: "bg-emerald-100 text-emerald-900 border-emerald-200",
    blurb:
      "This original Her Season explanation has been checked against recognised clinical guidance.",
  },
  clinician_reviewed: {
    label: "Clinician-reviewed",
    icon: BookOpen,
    className: "bg-sky-100 text-sky-900 border-sky-200",
    blurb: "Reviewed by a qualified healthcare professional.",
  },
  "clinician-reviewed": {
    label: "Clinician-reviewed",
    icon: BookOpen,
    className: "bg-sky-100 text-sky-900 border-sky-200",
    blurb: "Reviewed by a qualified healthcare professional.",
  },
  editorial: {
    label: "Editorial — pending clinical review",
    icon: Pencil,
    className: "bg-amber-100 text-amber-900 border-amber-200",
    blurb:
      "Original Her Season educational content. Not a substitute for individual medical advice.",
  },
};

function parseSources(raw: unknown): ClinicalSource[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as ClinicalSource[];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? format(d, "MMMM yyyy") : null;
}

export function ClinicalSourcesBadge({
  sources,
  clinical_grade,
  reviewed_at,
  reviewed_by,
  next_review_at,
  compact,
}: Props) {
  const [open, setOpen] = useState(false);
  const parsedSources = parseSources(sources);
  const grade = clinical_grade || "editorial";
  const meta = GRADE_META[grade] ?? GRADE_META.editorial;
  const Icon = meta.icon;

  const lastReviewed = formatDate(reviewed_at);
  const nextReview = formatDate(next_review_at);

  return (
    <div
      className={`rounded-lg border bg-muted/30 ${
        compact ? "p-3" : "p-4"
      } font-sans text-sm`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={`gap-1.5 ${meta.className}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {meta.label}
        </Badge>
        {lastReviewed ? (
          <span className="text-muted-foreground">
            Last reviewed{" "}
            <span className="text-foreground font-medium">{lastReviewed}</span>
            {reviewed_by ? ` · ${reviewed_by}` : ""}
          </span>
        ) : (
          <span className="text-muted-foreground italic">
            Not yet clinically reviewed
          </span>
        )}
        {nextReview && (
          <span className="text-muted-foreground">
            · Next review {nextReview}
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
        {meta.blurb}
      </p>

      <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
          {parsedSources.length > 0
            ? `Sources (${parsedSources.length})`
            : "About our sources"}
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-1.5">
          {parsedSources.length > 0 ? (
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              {parsedSources.map((s, i) => {
                const label = s.label || s.title || s.url || "Source";
                const org = s.publisher || s.organization;
                return (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-foreground">•</span>
                    <span>
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          {label}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-foreground">{label}</span>
                      )}
                      {org && <span> — {org}</span>}
                      {s.year && <span> ({s.year})</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground leading-relaxed">
              Her Season creates original educational content informed by
              recognised clinical guidance. Depending on the topic, we may review
              guidance from NICE (NG23), The Menopause Society, RANZCOG, the
              Australasian Menopause Society (AMS) and Jean Hailes for Women's
              Health. Specific citations for this article are being added as we
              complete our clinical review process. See our{" "}
              <a
                href="/editorial-standards"
                className="text-primary hover:underline"
              >
                Editorial &amp; Medical Standards
              </a>
              .
            </p>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default ClinicalSourcesBadge;
